import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import api, { setAuthToken } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { SignIn, SignOut, Spinner, FileText, DownloadSimple, Lock, ShieldCheck } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

const categoryColors = {
  general: "bg-[#4b7399]/20 text-[#4b7399] border-[#4b7399]",
  technical: "bg-[#4b7399]/20 text-[#4b7399] border-[#4b7399]",
  financial: "bg-[#d97706]/20 text-[#d97706] border-[#d97706]",
  legal: "bg-[#8c9eb3]/20 text-[#8c9eb3] border-[#8c9eb3]",
};

/* ─── LOGIN ─── */
function InvestorLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== "investor" && data.role !== "admin") { setError("This portal is for investors only."); return; }
      if (data.token) setAuthToken(data.token);
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="w-full max-w-md px-6">
        <div className="bg-[#121519] border border-[#2d3239] rounded-none p-10">
          <div className="flex items-center gap-3 mb-6">
            <Lock size={24} weight="regular" className="text-[#4b7399]" />
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399]">Secure Access</p>
              <h2 className="text-xl font-bold text-[#f2f4f8]">Investor Portal</h2>
            </div>
          </div>
          <p className="text-sm text-[#8c9eb3] mb-8">Access confidential technical and financial documents. Credentials are provided by the DustSentinel team.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="inv-email" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Email</Label>
              <Input id="inv-email" type="email" data-testid="investor-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] placeholder:text-[#8c9eb3]/50" />
            </div>
            <div>
              <Label htmlFor="inv-password" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Password</Label>
              <Input id="inv-password" type="password" data-testid="investor-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Provided by DustSentinel" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399] placeholder:text-[#8c9eb3]/50" />
            </div>
            {error && <p data-testid="investor-login-error" className="text-sm text-[#d97706] font-mono">{error}</p>}
            <button type="submit" disabled={loading} data-testid="investor-login-button" className="w-full bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none hover:bg-[#3a5a78] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Spinner size={16} className="animate-spin" /> : <SignIn size={16} />}
              {loading ? "Authenticating..." : "Access Documents"}
            </button>
          </form>
        </div>
      </motion.div>
    </main>
  );
}

/* ─── NDA GATE ─── */
function NDAAcceptance({ onAccept }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await api.post("/investor/accept-nda");
      onAccept();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  return (
    <main className="pt-16">
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <ShieldCheck size={28} weight="regular" className="text-[#4b7399]" />
              <div>
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399]">Required</p>
                <h1 className="text-2xl font-bold text-[#f2f4f8]">Confidentiality and Non-Disclosure Undertaking</h1>
              </div>
            </div>

            <div className="bg-[#121519] border border-[#2d3239] rounded-none p-6 mb-6">
              <p className="text-sm text-[#8c9eb3] leading-relaxed">
                Before accessing the DustSentinel investor portal, you must accept the confidentiality terms below. By continuing, you confirm that you are accessing the materials solely to assess a potential investment, funding or commercial opportunity relating to DustSentinel, and that you agree not to disclose, copy, distribute or misuse any information made available to you.
              </p>
            </div>

            <ScrollArea ref={scrollRef} className="h-[450px] border border-[#2d3239] rounded-none bg-[#0a0b0d] p-6 mb-6">
              <div className="space-y-6 text-sm text-[#8c9eb3] leading-relaxed pr-4">
                <p className="text-[#f2f4f8] font-semibold">DustSentinel Investor Portal — Confidentiality and Non-Disclosure Undertaking</p>
                <p>By ticking the box and clicking "I Agree", you confirm that you have read, understood and agree to be legally bound by this Confidentiality and Non-Disclosure Undertaking.</p>
                <p>This undertaking applies to all information, documents, data, presentations, financial models, forecasts, business plans, technical information, commercial information and other materials made available to you through the DustSentinel investor portal or otherwise provided in connection with DustSentinel.</p>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">1. Parties</p>
                  <p>This undertaking is given by you, the individual accessing the DustSentinel investor portal, and, where you are accessing the portal on behalf of a company, fund, family office, investment vehicle, advisory firm or other organisation, by that organisation as well.</p>
                  <p className="mt-2">"DustSentinel" means DustSentinel Environmental Systems Limited (Company No. 17220102), its affiliates, shareholders, directors, officers, employees, advisers, representatives and agents.</p>
                  <p className="mt-2">"Recipient" means the person or entity accessing the portal and receiving the Confidential Information.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">2. Purpose</p>
                  <p>The Confidential Information is being provided solely for the purpose of allowing the Recipient to review, assess and consider a potential investment, funding, commercial relationship, strategic transaction or other business opportunity relating to DustSentinel.</p>
                  <p className="mt-2">The Recipient must not use the Confidential Information for any other purpose.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">3. Confidential Information</p>
                  <p>Confidential Information includes, without limitation: investor decks, information memoranda and presentations; financial information, forecasts, assumptions, projections and models; technical information, product information, software, platform materials, specifications, designs, diagrams, processes and know-how; customer, supplier, partner, lender, investor and commercial pipeline information; business plans, strategy, pricing, revenue models, funding requirements and capital structure information; due diligence materials, correspondence, reports, notes, Q&A responses and management information; information relating to intellectual property, patents, trademarks, data, algorithms, source code, product roadmap and development plans; any information which by its nature should reasonably be understood to be confidential; and the existence and status of any discussions between the Recipient and DustSentinel.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">4. Recipient's Obligations</p>
                  <p>The Recipient agrees to: keep all Confidential Information strictly confidential; use the Confidential Information only for the Purpose stated above; not copy, reproduce, download, print, forward, publish, disclose, distribute or otherwise make available any Confidential Information except as expressly permitted under this undertaking; take reasonable care to protect the Confidential Information from unauthorised access, use, disclosure, loss or misuse; ensure that any person to whom Confidential Information is disclosed is bound by equivalent confidentiality obligations; notify DustSentinel immediately if the Recipient becomes aware of any unauthorised access, disclosure, loss or misuse of Confidential Information; not attempt to reverse engineer, decompile, replicate or copy any technical, software, product or platform information made available by DustSentinel; not use the Confidential Information to compete with, circumvent, damage or prejudice DustSentinel; and not contact DustSentinel's customers, suppliers, commercial partners, employees, advisers, funders or investors in relation to the opportunity without DustSentinel's prior written consent.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">5. Permitted Disclosure</p>
                  <p>The Recipient may disclose Confidential Information only to its directors, officers, employees, partners, members, investment committee, professional advisers, funders or representatives who strictly need to know the information for the Purpose. The Recipient remains responsible for any breach of this undertaking by any person to whom it discloses Confidential Information.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">6. Exclusions</p>
                  <p>Confidential Information does not include information which the Recipient can demonstrate: is or becomes publicly available other than through breach of this undertaking; was lawfully known to the Recipient before it was disclosed by DustSentinel; was lawfully received from a third party without breach of any duty of confidentiality; or was independently developed by the Recipient without use of or reference to the Confidential Information.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">7. No Representation or Warranty</p>
                  <p>The Confidential Information is provided for information and discussion purposes only. DustSentinel does not make any representation or warranty, express or implied, as to the accuracy, completeness or reliability of the Confidential Information. The Recipient acknowledges that any investment or commercial decision must be based on its own independent review, due diligence, professional advice and assessment. Nothing in the Confidential Information constitutes an offer, commitment, recommendation, financial promotion, legal advice, tax advice or investment advice.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">8. No Licence or Transfer of Rights</p>
                  <p>All Confidential Information remains the property of DustSentinel or the relevant rights holder. No licence, assignment or transfer of any intellectual property rights, commercial rights or other rights is granted to the Recipient by reason of access to the portal or disclosure of the Confidential Information.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">9. Return or Destruction</p>
                  <p>At DustSentinel's request, the Recipient must promptly return, delete or destroy all Confidential Information in its possession or control, including any copies, extracts, summaries or notes derived from it. The Recipient may retain one copy only where required by law, regulation, internal compliance policy or professional obligations, provided that such retained copy remains subject to this undertaking.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">10. Duration</p>
                  <p>The Recipient's confidentiality obligations continue for a period of five years from the date of acceptance of this undertaking. Confidential Information relating to trade secrets, proprietary technology, source code, technical know-how, intellectual property, algorithms, product design, inventions and commercially sensitive strategy shall remain confidential for so long as it remains confidential in nature.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">11. Portal Access and Audit Trail</p>
                  <p>The Recipient acknowledges that DustSentinel may record and retain details of portal access, including name, email address, organisation, IP address, time and date of access, documents viewed, documents downloaded, acceptance of this undertaking and other activity logs. The Recipient agrees that such records may be used by DustSentinel to evidence acceptance of this undertaking and monitor compliance with confidentiality obligations.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">12. Injunctive Relief</p>
                  <p>The Recipient acknowledges that unauthorised disclosure or misuse of the Confidential Information may cause serious and irreparable harm to DustSentinel. DustSentinel shall be entitled to seek injunctive relief, specific performance or other equitable remedies, in addition to any other remedies available at law.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">13. Authority to Accept</p>
                  <p>By clicking "I Agree", the individual accepting this undertaking confirms that: they are legally capable of entering into this undertaking; they are authorised to accept this undertaking on behalf of the organisation they represent, if applicable; and the information provided during portal registration is accurate and complete.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">14. Governing Law and Jurisdiction</p>
                  <p>This undertaking and any dispute or claim arising from or in connection with it shall be governed by the laws of England and Wales. The courts of England and Wales shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with this undertaking.</p>
                </div>

                <div>
                  <p className="text-[#f2f4f8] font-semibold mb-2">15. Acceptance</p>
                  <p>By ticking the box and clicking "I Agree", you agree to be legally bound by this Confidentiality and Non-Disclosure Undertaking.</p>
                </div>

                <p className="font-mono text-xs text-[#8c9eb3] mt-4">Version 1.0 — 11 May 2026</p>
              </div>
            </ScrollArea>

            {/* Checkbox + Button */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="nda-checkbox"
                  data-testid="nda-checkbox"
                  checked={checked}
                  onCheckedChange={(val) => setChecked(val === true)}
                  className="mt-0.5 rounded-none border-[#2d3239] data-[state=checked]:bg-[#4b7399] data-[state=checked]:border-[#4b7399]"
                />
                <label htmlFor="nda-checkbox" className="text-sm text-[#f2f4f8] leading-relaxed cursor-pointer select-none">
                  I confirm that I have read, understood and agree to be legally bound by the DustSentinel Confidentiality and Non-Disclosure Undertaking.
                </label>
              </div>

              <button
                onClick={handleAccept}
                disabled={!checked || submitting}
                data-testid="nda-accept-button"
                className="w-full bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none hover:bg-[#3a5a78] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? <Spinner size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {submitting ? "Processing..." : "I Agree — Access Investor Portal"}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

/* ─── DOCUMENT ROOM ─── */
function DocumentRoom({ user, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const { data } = await api.get("/investor/documents");
      setDocuments(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <section data-testid="investor-portal-section" className="py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-2">Investor Portal</p>
            <h1 className="text-2xl md:text-3xl font-bold text-[#f2f4f8]">Document Room</h1>
            <p className="text-sm text-[#8c9eb3] mt-2">Welcome, {user.name}. {user.company && `(${user.company})`}</p>
          </div>
          <button onClick={onLogout} data-testid="investor-logout-button" className="font-mono text-xs tracking-wider uppercase text-[#8c9eb3] hover:text-[#f2f4f8] transition-colors flex items-center gap-2">
            <SignOut size={16} /> Logout
          </button>
        </div>

        {/* Built-in documents */}
        <div className="mb-8">
          <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-4">System Documents</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a href={`${API_BASE}/api/downloads/architecture-sheet`} target="_blank" rel="noopener noreferrer" className="group bg-[#121519] border border-[#2d3239] rounded-none p-6 hover:border-[#4b7399] transition-colors block">
              <div className="flex items-start gap-4">
                <FileText size={28} weight="regular" className="text-[#4b7399] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-sm font-semibold text-[#f2f4f8] mb-1 group-hover:text-[#4b7399] transition-colors">System Architecture Overview</h3>
                  <p className="text-xs text-[#8c9eb3]">Subsystem architecture, modules, operational states, and deployment environments.</p>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-[#4b7399] mt-3 inline-flex items-center gap-1"><DownloadSimple size={12} /> Download PDF</span>
                </div>
              </div>
            </a>
            <a href={`${API_BASE}/api/downloads/concept-note`} target="_blank" rel="noopener noreferrer" className="group bg-[#121519] border border-[#2d3239] rounded-none p-6 hover:border-[#4b7399] transition-colors block">
              <div className="flex items-start gap-4">
                <FileText size={28} weight="regular" className="text-[#4b7399] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-sm font-semibold text-[#f2f4f8] mb-1 group-hover:text-[#4b7399] transition-colors">Technical Concept Note</h3>
                  <p className="text-xs text-[#8c9eb3]">Problem statement, NASA requirement basis, and the DustSentinel-LX approach.</p>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-[#4b7399] mt-3 inline-flex items-center gap-1"><DownloadSimple size={12} /> Download PDF</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Shared documents */}
        {loading ? (
          <div className="flex justify-center py-12"><Spinner size={24} className="text-[#4b7399] animate-spin" /></div>
        ) : documents.length > 0 ? (
          <div>
            <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-4">Shared Documents</p>
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-[#121519] border border-[#2d3239] rounded-none p-5 hover:border-[#4b7399] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <FileText size={24} weight="regular" className="text-[#4b7399] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="text-sm font-semibold text-[#f2f4f8]">{doc.title}</h4>
                          <Badge className={`rounded-none font-mono text-[10px] ${categoryColors[doc.category] || categoryColors.general}`} variant="outline">{doc.category}</Badge>
                        </div>
                        <p className="text-xs text-[#8c9eb3]">{doc.description}</p>
                      </div>
                    </div>
                    {doc.url && (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] tracking-wider uppercase text-[#4b7399] hover:text-[#f2f4f8] transition-colors flex items-center gap-1 flex-shrink-0">
                        <DownloadSimple size={14} /> View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-12 border-t border-[#2d3239] pt-8">
          <p className="font-mono text-[10px] tracking-wider uppercase text-[#8c9eb3]">
            Confidential — These materials are shared under NDA. Do not distribute without written consent from DustSentinel.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── MAIN ─── */
export default function InvestorPortalPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaChecking, setNdaChecking] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        if (data.role === "investor" || data.role === "admin") {
          setUser(data);
          checkNda();
        } else {
          setChecking(false);
        }
      })
      .catch(() => setChecking(false));
  }, []);

  const checkNda = async () => {
    setNdaChecking(true);
    try {
      const { data } = await api.get("/investor/nda-status");
      setNdaAccepted(data.accepted);
    } catch (err) { console.error(err); }
    finally { setNdaChecking(false); setChecking(false); }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout");
    setAuthToken(null);
    setUser(null);
    setNdaAccepted(false);
  };

  if (checking || ndaChecking) return <main className="pt-16 min-h-screen flex items-center justify-center"><Spinner size={32} className="text-[#4b7399] animate-spin" /></main>;
  if (!user) return <InvestorLogin onLogin={(u) => { setUser(u); checkNda(); }} />;
  if (!ndaAccepted) return <NDAAcceptance onAccept={() => setNdaAccepted(true)} />;

  return (
    <main className="pt-16">
      <DocumentRoom user={user} onLogout={handleLogout} />
    </main>
  );
}
